import React from "react";
import { Box, Typography, Switch, Link, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { themes } from "services/constants";
import {
  SftDeductSalaryForAbsenceExample,
  boxStyle,
  CustomBox,
  individualBoxStyle,
  infoMessageStyle,
  additionalInfoStyle,
  mainOptions,
} from "../ShiftCategoryOptions";

export const DeductSalaryForAbsence = ({
  watch,
  setValue,
  register,
  errors,
  resetField,
  isBandAllowed,
  absencePaid,
  setAbsencePaid,
  setClearAbsenceValues,
  earningDeductionPriorityRows,
  setAbsenceDeductionPriorityRows,
}) => (
  <Box sx={mainOptions}>
    <Box sx={boxStyle(watch("deduct_salary_for_absence"))}>
      <Typography fontWeight="medium">Deduct salary for absence</Typography>
      <Switch
        checked={watch("deduct_salary_for_absence")}
        onChange={(event) => {
          const checked = event.target.checked;
          setValue("deduct_salary_for_absence", checked);
          if (!checked) {
            const fields = [
              "deduction",
              "additional_deduction",
              "extra_deduction",
              "extra_additional_deduction",
            ];

            fields.forEach((field) => {
              resetField(field, { keepError: false });
              setValue(field, 0, { shouldValidate: false });
            });
            setValue("is_deduction_priority_enabled", true);
            setAbsenceDeductionPriorityRows(earningDeductionPriorityRows);
          }
        }}
      />
    </Box>
    {watch("deduct_salary_for_absence") && absencePaid && (
      <Box
        sx={{
          backgroundColor: "#fff6e5",
          borderRadius: 2,
          p: 1.5,
          position: "relative",
          mb: 2,
          ml: 2,
          mr: 2,
          border: `1px solid ${themes.miliedLightGray}`,
        }}
      >
        <Typography variant="body2">
          <strong>Absence Paid</strong> is on.{" "}
          <Link
            component="button"
            underline="hover"
            color="success.main"
            onClick={() => {
              setAbsencePaid(false);
              setClearAbsenceValues(true);
            }}
            sx={{ fontWeight: 500, pb: 0.5 }}
          >
            Click here
          </Link>{" "}
          to turn it off and apply the deduction.
        </Typography>

        <IconButton
          size="small"
          onClick={() => {
            setAbsencePaid(false);
          }}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            color: "#c62828",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    )}
    {watch("deduct_salary_for_absence") && (
      <>
        <Box sx={{ ml: 2, mb: 2 }}>
          <Box>
            <Typography fontWeight="medium">
              Apply penalty for absence
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enable this option to deduct additional salary days for each day
              of absence, on top of the normal deduction.
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ ml: 2, mb: 2 }}
        >
          <Box>
            <Typography fontWeight="medium">
              Consider unpaid leave for absence penalty
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When enabled, the system will include unpaid leave days when
              calculating the total absence penalty for the payroll cycle.
            </Typography>
          </Box>
          <Box sx={{ mr: 1.6 }}>
            <Switch
              {...register("consider_unpaid_leave_for_absent_penalty")}
              checked={watch("consider_unpaid_leave_for_absent_penalty")}
              onChange={(event) => {
                const checked = event.target.checked;
                setValue("consider_unpaid_leave_for_absent_penalty", checked);
              }}
            />
          </Box>
        </Box>

        <Box sx={additionalInfoStyle}>
          <Box sx={{ ...individualBoxStyle, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              If an employee is absent for
            </Typography>
            <CustomBox
              name="deduction"
              type="shift"
              value={watch("deduction")}
              register={register}
              errors={errors}
              handleChange={(e, value) => {
                setValue("deduction", value);
              }}
              rules={{ required: "Enter value" }}
              inputWidth="50px"
            />

            <Typography variant="body2" color="text.secondary">
              day deduct an additional
            </Typography>
            <CustomBox
              name="additional_deduction"
              type="shift"
              value={watch("additional_deduction")}
              register={register}
              errors={errors}
              handleChange={(e, value) => {
                setValue("additional_deduction", value);
              }}
              rules={{ required: "Enter value" }}
              inputWidth="50px"
            />
            <Typography variant="body2" color="text.secondary">
              day as penalty.
            </Typography>
          </Box>
        </Box>
        <Box sx={additionalInfoStyle}>
          <Box sx={individualBoxStyle}>
            <Typography variant="body2" color="text.secondary">
              For every extra
            </Typography>
            <CustomBox
              name="extra_deduction"
              type="shift"
              value={watch("extra_deduction")}
              register={register}
              errors={errors}
              handleChange={(e, value) => {
                setValue("extra_deduction", value);
              }}
              rules={{ required: "Enter value" }}
              inputWidth="50px"
            />

            <Typography variant="body2" color="text.secondary">
              day of absence,deduct
            </Typography>
            <CustomBox
              name="extra_additional_deduction"
              type="shift"
              value={watch("extra_additional_deduction")}
              register={register}
              errors={errors}
              handleChange={(e, value) => {
                setValue("extra_additional_deduction", value);
              }}
              rules={{ required: "Enter value" }}
              inputWidth="50px"
            />
            <Typography variant="body2" color="text.secondary">
              day as penalty.
            </Typography>
          </Box>
        </Box>
        <Box sx={infoMessageStyle}>
          <SftDeductSalaryForAbsenceExample />
        </Box>

        {isBandAllowed && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ ml: 2, mt: 2 }}
          >
            <Box>
              <Typography fontWeight="medium">
                Reduce Overtime Incentive for Absence Penalty
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, if an employee exceeds the permitted absence
                limit, all overtime worked on normal working days, weekly
                offs, and holidays will be paid at 1×, regardless of the
                applicable overtime multiplier. This includes reducing 1.5×
                overtime on normal working days, 2× overtime on weekly offs
                or holidays, and 2.5× evening overtime to 1×.
              </Typography>
            </Box>
            <Box sx={{ mr: 1.6 }}>
              <Switch
                {...register("is_absence_penalty_band_hours_paid")}
                checked={watch("is_absence_penalty_band_hours_paid")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_absence_penalty_band_hours_paid", checked);
                }}
              />
            </Box>
          </Box>
        )}
      </>
    )}
  </Box>
);
